import { CalendarDate } from '@internationalized/date';
import {
  enumLoadSortOption,
  enumLoadStatus,
  LoadSortOption,
  LoadStatus,
} from '@rematter/frontend-lib-api-client';
import { DataGridSelection } from '@rematter/ui-web';
import { atom } from 'jotai';

import { loadBulkVoidAtom } from '../_graphql/mutationLoadBulkVoid';
import { loadMarkAsReceivedAtom } from '../_graphql/mutationLoadMarkAsReceived';
import { loadMarkAsShippedAtom } from '../_graphql/mutationLoadMarkAsShipped';
import { loadMarkAsVoidedAtom } from '../_graphql/mutationLoadMarkAsVoided';
import { loadReconcileAtom } from '../_graphql/mutationLoadReconcile';
import { loadsListOptimizedQueryAtom } from '../_graphql/queryLoadsListOptimized';
import { loadStatusCountQueryAtom } from '../_graphql/queryLoadStatusCount';
import { transformLoadLineItemsToReceivedLineItems } from '../utils/load';

import { LoadRecord } from '~/modules/trade/types/load';
import { atomFromQueryString } from '~/shared/state';

// 🟣🟣🟣 PARAMS (simplified to match customer portal pattern)
export const $searchTerm = atomFromQueryString.string('q');
export const $selectedStatus = atomFromQueryString.string<string>('s');
export const $selectedFacility = atomFromQueryString.string('f');
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);
export const $dateRange = atomFromQueryString.dateRange();
export const $selectedCreatedByUserIds =
  atomFromQueryString.stringSet('createdByIds');

// 🟣🟣🟣 COLUMN FILTER PARAMS
export const $columnDateRange = atomFromQueryString.dateRange();
export const $columnSelectedCustomerIds =
  atomFromQueryString.stringSet('colCustomers');
export const $columnSelectedFacilityIds =
  atomFromQueryString.stringSet('colFacilities');
export const $columnSelectedStatuses =
  atomFromQueryString.stringSet<LoadStatus>('colStatuses');
export const $selectedLoadIdForFilter = atomFromQueryString.string('loadId');

// 🔘🔘🔘 PLAIN ATOMS
export const $selectedKeys = atom<DataGridSelection>(new Set<React.Key>());
export const $isDialogOpen = atom<boolean>(false);
export const $isReconciliationMode = atom<boolean>(false);
export const $selectedLoad = atom<LoadRecord | null>(null);
export const $isVoidDialogOpen = atom<boolean>(false);
export const $selectedLoadIdForShipped = atom<string | null>(null);
export const $selectedLoadIdForReceived = atom<string | null>(null);
export const $currentSorting = atom<LoadSortOption[]>([
  enumLoadSortOption.CreatedAtDesc,
]);

// Bulk void dialog state
type Load = { id: string; displayNumber: string; status: LoadStatus };
export const $isBulkVoidDialogOpen = atom<boolean>(false);
export const $bulkVoidLoads = atom<Load[]>([]);

// 🟢🟢🟢 HELPER -> Validation interface for VoidEntityDialog
interface AffectedLoad {
  id: string;
  documentNumber: string;
  status: LoadStatus;
}

export interface LoadVoidValidation {
  loadId: string;
  canVoid: boolean;
  requiresForceUnlink: boolean;
  blockingLoads: AffectedLoad[];
  unlinkableLoads: AffectedLoad[];
  currentStatus?: LoadStatus;
}

// 🟢🟢🟢 HELPER -> Create fetch validation function for VoidEntityDialog
// Loads can always be voided (no blocking loads), so this is a simple passthrough
export const createLoadsVoidValidationFetcher =
  () =>
  async (loadIds: string[]): Promise<Map<string, LoadVoidValidation>> => {
    const results = new Map<string, LoadVoidValidation>();

    for (const loadId of loadIds) {
      results.set(loadId, {
        loadId,
        canVoid: true,
        requiresForceUnlink: false,
        blockingLoads: [],
        unlinkableLoads: [],
      });
    }

    return results;
  };

// 🟢🟢🟢 COMPUTED DATA -> status counts query params
export const $statusCountsParams = atom((get) => {
  const searchTerm = get($searchTerm);
  const dateRange = get($dateRange);

  const params: {
    searchTerm?: string;
    fromExpectedShipDate?: CalendarDate;
    toExpectedShipDate?: CalendarDate;
  } = {};

  if (searchTerm) params.searchTerm = searchTerm;
  if (dateRange?.start) {
    params.fromExpectedShipDate = dateRange.start as CalendarDate;
  }
  if (dateRange?.end) {
    params.toExpectedShipDate = dateRange.end as CalendarDate;
  }

  return params;
});

// 🟢🟢🟢 COMPUTED DATA -> real statuses array by selected status filter
export const $statuses = atom((get) => {
  const selectedStatus = get($selectedStatus);

  if (selectedStatus === 'all' || !selectedStatus) return [];

  const statusFilterOptions: { key: string; value: LoadStatus[] }[] = [
    {
      key: 'all',
      value: [],
    },
    {
      key: 'unassigned',
      value: [enumLoadStatus.Unassigned],
    },
    {
      key: 'pending',
      value: [enumLoadStatus.Pending],
    },
    {
      key: 'shipped',
      value: [enumLoadStatus.Shipped, enumLoadStatus.Received],
    },
    {
      key: 'closed',
      value: [enumLoadStatus.Reconciled, enumLoadStatus.Voided],
    },
  ];

  return (
    statusFilterOptions.find((option) => option.key === selectedStatus)
      ?.value ?? []
  );
});

// 🟢🟢🟢 REMOTE DATA
export const $remoteLoads = loadsListOptimizedQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const selectedFacility = get($selectedFacility);
  const selectedStatuses = get($statuses);
  const dateRange = get($dateRange);
  const limit = get($limit);
  const offset = get($offset);
  const selectedLoadIdForFilter = get($selectedLoadIdForFilter);
  const createdByUserIds = get($selectedCreatedByUserIds);
  const sortingParams = get($currentSorting);

  // Column filters
  const columnDateRange = get($columnDateRange);
  const columnSelectedCustomerIds = get($columnSelectedCustomerIds);
  const columnSelectedFacilityIds = get($columnSelectedFacilityIds);
  const columnSelectedStatuses = get($columnSelectedStatuses);

  const params: {
    searchTerm?: string;
    status: LoadStatus[];
    facilityId?: string;
    facilityIds?: string[];
    accountIds?: string[];
    fromExpectedShipDate?: CalendarDate;
    toExpectedShipDate?: CalendarDate;
    fromColumnDate?: CalendarDate;
    toColumnDate?: CalendarDate;
    columnStatuses?: LoadStatus[];
    ids?: string[];
    createdByUserIds?: string[];
    hasSupplier?: boolean;
    limit: number;
    offset: number;
    sorting: LoadSortOption[];
  } = {
    limit,
    offset,
    sorting: sortingParams,
    status: selectedStatuses,
    hasSupplier: false,
  };

  // Only include searchTerm if it's not empty
  if (searchTerm?.trim()) params.searchTerm = searchTerm;

  // Include selected load ID if available
  if (selectedLoadIdForFilter) params.ids = [selectedLoadIdForFilter];
  if (selectedFacility && selectedFacility !== 'all')
    params.facilityId = selectedFacility;
  if (dateRange?.start) {
    params.fromExpectedShipDate = dateRange.start as CalendarDate;
  }
  if (dateRange?.end) {
    params.toExpectedShipDate = dateRange.end as CalendarDate;
  }

  // Column filters
  if (columnSelectedCustomerIds.size > 0) {
    params.accountIds = Array.from(columnSelectedCustomerIds);
  }
  if (columnSelectedFacilityIds.size > 0) {
    params.facilityIds = Array.from(columnSelectedFacilityIds);
  }
  if (columnSelectedStatuses.size > 0) {
    params.columnStatuses = Array.from(columnSelectedStatuses) as LoadStatus[];
  }
  if (columnDateRange?.start) {
    params.fromColumnDate = columnDateRange.start as CalendarDate;
  }
  if (columnDateRange?.end) {
    params.toColumnDate = columnDateRange.end as CalendarDate;
  }

  // Created By filter
  if (createdByUserIds.size > 0) {
    params.createdByUserIds = Array.from(createdByUserIds);
  }

  return {
    variables: params,
  };
});

// 🟢🟢🟢 REMOTE DATA
export const $loadsCountingByStatus = loadStatusCountQueryAtom();

// 🟢🟢🟢 COMPUTED DATA -> received line items
export const $receivedLineItems = atom((get) => {
  const id = get($selectedLoadIdForReceived);
  if (!id) return [];

  const loads = get($remoteLoads);
  const load = loads?.data?.loads.find((load) => load.id === id);
  return transformLoadLineItemsToReceivedLineItems(load?.lineItems ?? []);
});

// 🟢🟢🟢 COMPUTED DATA -> booking id by load id
export const $bookingIdByLoadId = atom((get) => {
  const loads = get($remoteLoads);

  return (loadId: string) => {
    const load = loads?.data?.loads.find((load) => load.id === loadId);
    return load?.bookingId;
  };
});

// 🟢🟢🟢 COMPUTED DATA -> filters active
export const $filtersActive = atom((get) => {
  const searchTerm = get($searchTerm);
  const selectedFacility = get($selectedFacility);
  const dateRange = get($dateRange);
  const selectedLoadIdForFilter = get($selectedLoadIdForFilter);
  const createdByUserIds = get($selectedCreatedByUserIds);

  return (
    (searchTerm !== undefined && searchTerm?.trim() !== '') ||
    (selectedFacility !== undefined && selectedFacility !== 'all') ||
    !!dateRange ||
    !!selectedLoadIdForFilter ||
    createdByUserIds.size > 0
  );
});

// 🟠🟠🟠 ACTION -> VOID LOAD
export const $voidLoad = loadMarkAsVoidedAtom({
  onSuccess: (get) => {
    // Refetch loads after successful void
    get($remoteLoads).refetch?.();
    get($loadsCountingByStatus).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> MARK AS SHIPPED
export const $markAsShipped = loadMarkAsShippedAtom({
  onSuccess: (get) => {
    get($remoteLoads).refetch?.();
    get($loadsCountingByStatus).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> MARK AS RECEIVED
export const $markAsReceived = loadMarkAsReceivedAtom({
  onSuccess: (get) => {
    get($remoteLoads).refetch?.();
    get($loadsCountingByStatus).refetch?.();
  },
});
// 🟠🟠🟠 ACTION -> MARK AS RECONCILED
export const $markAsReconciled = loadReconcileAtom({
  onSuccess: (get) => {
    get($remoteLoads).refetch?.();
    get($loadsCountingByStatus).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> BULK VOID LOADS
export const $bulkVoidLoad = loadBulkVoidAtom({
  onSuccess: (get) => {
    get($remoteLoads).refetch?.();
    get($loadsCountingByStatus).refetch?.();
  },
});
